<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import Button from "../components/Button.vue";
import Icon from "../components/Icon.vue";

export interface AppShellNavItem {
  key: string;
  label: string;
  icon?: string;
  href?: string;
}

export interface AppShellNavGroup {
  label: string;
  items: AppShellNavItem[];
}

export interface AppShellUser {
  name: string;
  meta?: string;
  initials?: string;
}

const props = withDefaults(defineProps<{
  groups: AppShellNavGroup[];
  activeKey?: string;
  brandName?: string;
  brandMark?: string;
  brandImage?: string;
  user: AppShellUser;
}>(), {
  activeKey: "",
  brandName: "RiskOps 风控中心",
  brandMark: "R",
  brandImage: "",
});

const emit = defineEmits<{
  navigate: [item: AppShellNavItem];
  userAction: [action: "profile" | "settings"];
  toggleTheme: [];
  logout: [];
}>();

const menuOpen = ref(false);
const closeMenu = () => { menuOpen.value = false; };
onMounted(() => document.addEventListener("click", closeMenu));
onBeforeUnmount(() => document.removeEventListener("click", closeMenu));

function navigate(item: AppShellNavItem) {
  emit("navigate", item);
}
</script>

<template>
  <div class="ro-shell">
    <aside class="ro-sidebar">
      <div class="ro-brand">
        <img v-if="props.brandImage" class="ro-brand__image" :src="props.brandImage" alt="">
        <span v-else class="ro-brand__mark">{{ props.brandMark }}</span>
        <span class="ro-brand__name">{{ props.brandName }}</span>
      </div>
      <div class="ro-sidebar__scroll">
        <section v-for="group in props.groups" :key="group.label" class="ro-nav-group">
          <div class="ro-nav-group__title">{{ group.label }}</div>
          <a
            v-for="item in group.items"
            :key="item.key"
            class="ro-nav-item"
            :class="{ 'is-active': item.key === props.activeKey }"
            :href="item.href || '#'"
            @click.prevent="navigate(item)"
          >
            <Icon class="ro-nav-item__icon" :name="item.icon || 'folder'" />
            <span class="ro-nav-item__label">{{ item.label }}</span>
          </a>
        </section>
      </div>
      <div class="ro-user" @click.stop>
        <button class="ro-avatar" type="button" @click="menuOpen = !menuOpen">
          {{ props.user.initials || props.user.name.slice(0, 1) }}
        </button>
        <div class="ro-user__copy" @click="menuOpen = !menuOpen">
          <strong>{{ props.user.name }}</strong>
          <small>{{ props.user.meta }}</small>
        </div>
        <div v-if="menuOpen" class="ro-user-menu">
          <Button variant="ghost" type="button" icon="user" @click="emit('userAction', 'profile'); closeMenu()">个人中心</Button>
          <Button variant="ghost" type="button" icon="settings" @click="emit('userAction', 'settings'); closeMenu()">设置</Button>
          <Button variant="ghost" type="button" icon="moon" @click="emit('toggleTheme'); closeMenu()">模式切换</Button>
          <Button variant="ghost" type="button" icon="log-out" @click="emit('logout'); closeMenu()">退出登录</Button>
        </div>
      </div>
    </aside>
    <main class="ro-main">
      <div v-if="$slots.topbar" class="ro-topbar"><slot name="topbar" /></div>
      <div class="ro-content"><slot /></div>
    </main>
  </div>
</template>
